import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, BellRinging, BellSlash, X } from "@phosphor-icons/react";
import { isPushReady, getCurrentPushSubscription, enablePush, disablePush, sendTestPush } from "@/lib/push";
import { toast } from "sonner";

/**
 * "Get the app" + "Enable notifications" floating card.
 * Appears bottom-right on app shell when:
 *  - browser supports beforeinstallprompt (Chromium Android/desktop) and app is not installed, OR
 *  - notifications are supported but not granted yet.
 * Dismissible — state persisted to localStorage so it doesn't nag.
 */
export default function PwaActionCard() {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [pushState, setPushState] = useState("idle"); // "idle" | "granted" | "subscribed" | "unsupported"
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("clariva_pwa_card_dismiss") === "1");

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    const onInstalled = () => { setInstalled(true); setInstallEvent(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    (async () => {
      if (!(await isPushReady())) { setPushState("unsupported"); return; }
      const sub = await getCurrentPushSubscription();
      if (sub) setPushState("subscribed");
      else if (Notification.permission === "granted") setPushState("granted");
    })();
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const c = await installEvent.userChoice;
    if (c.outcome === "accepted") toast.success("Clariva installed");
    setInstallEvent(null);
  };

  const enable = async () => {
    try {
      await enablePush();
      setPushState("subscribed");
      toast.success("Notifications enabled — we'll ping you on approvals");
      // Fire a test push so the user sees it works
      setTimeout(() => { sendTestPush().catch(() => {}); }, 800);
    } catch (e) { toast.error(e.message || "Could not enable notifications"); }
  };

  const disable = async () => {
    await disablePush();
    setPushState("idle");
    toast.success("Notifications turned off");
  };

  const dismiss = () => { localStorage.setItem("clariva_pwa_card_dismiss", "1"); setDismissed(true); };

  // Hide entirely when nothing to do
  if (dismissed) return null;
  const showInstall = !installed && installEvent;
  const showPush = pushState === "idle";
  if (!showInstall && !showPush) return null;

  return (
    <div
      data-testid="pwa-action-card"
      className="fixed bottom-20 lg:bottom-4 right-4 z-50 w-72 rounded-xl glass-strong border border-[var(--border-strong)] shadow-2xl p-4"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="pwa-card-dismiss"
        aria-label="Dismiss"
        className="absolute top-2 right-2 w-6 h-6 rounded hover:bg-[var(--surface-2)] text-[var(--text-3)] flex items-center justify-center"
      >
        <X size={12} />
      </button>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--accent)] mb-2">Make Clariva feel native</div>
      <div className="space-y-2">
        {showInstall && (
          <Button size="sm" onClick={install} data-testid="pwa-install-btn" className="w-full gap-2 justify-start">
            <DownloadSimple size={13} weight="duotone" /> Install Clariva
          </Button>
        )}
        {showPush && (
          <Button variant="outline" size="sm" onClick={enable} data-testid="pwa-push-enable-btn" className="w-full gap-2 justify-start">
            <BellRinging size={13} weight="duotone" /> Enable approval notifications
          </Button>
        )}
        {pushState === "subscribed" && (
          <Button variant="ghost" size="sm" onClick={disable} data-testid="pwa-push-disable-btn" className="w-full gap-2 justify-start text-[var(--text-3)]">
            <BellSlash size={13} /> Notifications on — turn off
          </Button>
        )}
      </div>
    </div>
  );
}
